import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { STYLE_REGISTRY } from '@/lib/style-config';

interface SearchFilters {
  query: string;
  // Future extensibility: priceRange, availability, etc.
}

interface CityResult {
  city: string;
  city_slug: string;
  country: string;
  studio_count: number;
}

interface ArtistResult {
  id: string;
  artist_name: string;
  studio_name: string | null;
  slug: string | null;
  city: string | null;
  is_verified: boolean;
}

interface StudioResult {
  id: string;
  name: string;
  city: string;
  city_slug: string;
  slug: string | null;
  country: string;
}

export interface StyleResult {
  id: string;
  label: string;
  labelKey: string;
}

export interface SearchResults {
  cities: CityResult[];
  artists: ArtistResult[];
  studios: StudioResult[];
  styles: StyleResult[];
  isLoading: boolean;
  hasResults: boolean;
  noResults: boolean;
}

export function useSearch(filters: SearchFilters): SearchResults {
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters.query), 280);
    return () => clearTimeout(t);
  }, [filters.query]);

  const enabled = debounced.length >= 2;

  const { data: cities = [], isFetching: citiesLoading } = useQuery({
    queryKey: ['search-cities', debounced],
    queryFn: async () => {
      const { data } = await supabase
        .from('city_stats')
        .select('city, city_slug, country, studio_count')
        .ilike('city', `%${debounced}%`)
        .order('studio_count', { ascending: false })
        .limit(5);
      return (data || []) as CityResult[];
    },
    enabled,
  });

  const { data: artists = [], isFetching: artistsLoading } = useQuery({
    queryKey: ['search-artists', debounced],
    queryFn: async () => {
      const [byArtist, byStudio] = await Promise.all([
        supabase
          .from('artist_profiles')
          .select('id, artist_name, studio_name, slug, city, is_verified')
          .ilike('artist_name', `%${debounced}%`)
          .limit(3),
        supabase
          .from('artist_profiles')
          .select('id, artist_name, studio_name, slug, city, is_verified')
          .ilike('studio_name', `%${debounced}%`)
          .limit(3),
      ]);
      const combined = [...(byArtist.data || []), ...(byStudio.data || [])];
      return Array.from(new Map(combined.map(a => [a.id, a])).values()).slice(0, 5) as ArtistResult[];
    },
    enabled,
  });

  const { data: studios = [] } = useQuery({
    queryKey: ['search-studios', debounced],
    queryFn: async () => {
      const { data } = await supabase
        .from('unclaimed_profiles')
        .select('id, name, city, city_slug, slug, country')
        .is('claimed_by', null)
        .eq('is_closed', false)
        .neq('business_type', 'noise')
        .ilike('name', `%${debounced}%`)
        .order('google_rating', { ascending: false })
        .limit(4);
      return (data || []) as StudioResult[];
    },
    enabled,
  });

  // Style matching (local, no DB query needed)
  const styles: StyleResult[] = enabled
    ? STYLE_REGISTRY
        .filter(s => s.isActive)
        .filter(s => {
          const q = debounced.toLowerCase();
          const label = s.labelKey.replace('style.', '').replace(/_/g, ' ');
          return s.id.includes(q) || label.includes(q);
        })
        .slice(0, 4)
        .map(s => ({ id: s.id, label: s.labelKey.replace('style.', '').replace(/_/g, ' '), labelKey: s.labelKey }))
    : [];

  const isLoading = citiesLoading || artistsLoading;
  const hasResults = cities.length > 0 || artists.length > 0 || studios.length > 0 || styles.length > 0;
  const noResults = enabled && !isLoading && !hasResults;

  return { cities, artists, studios, styles, isLoading, hasResults, noResults };
}
